import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown, MessageSquare } from 'lucide-react';

const faqData = [
  {
    category: 'Project Process',
    questions: [
      {
        q: 'How does the project process work?',
        a: 'Our process is simple and structured: 1) Inquiry - You submit your project details through our form. 2) Review - We review your brief and send a custom proposal with timeline and pricing. 3) Creation - Once the deposit is received, we begin designing with regular check-ins. 4) Delivery - After revisions and final approval, you receive all deliverables.',
      },
      {
        q: 'What information do you need to get started?',
        a: 'To begin, we need a clear understanding of your project goals, target audience, any brand guidelines or preferences, reference examples you like, and your timeline. The more details you provide, the better we can serve you.',
      },
    ],
  },
  {
    category: 'Timelines',
    questions: [
      {
        q: 'How long does a typical project take?',
        a: 'Timelines vary depending on project complexity. Logo designs typically take 1-2 weeks, full brand identities 2-4 weeks, social media packages 1-2 weeks, and web designs 3-6 weeks. Rush projects can be accommodated for an additional fee.',
      },
      {
        q: 'What happens if I need my project completed faster?',
        a: 'We offer rush services for time-sensitive projects. Rush fees typically range from 25-50% of the project cost depending on how quickly you need it. Contact us to discuss your specific timeline.',
      },
    ],
  },
  {
    category: 'Revisions',
    questions: [
      {
        q: 'How many revisions are included?',
        a: 'Each project includes two (2) rounds of revisions. This allows us to refine the design based on your feedback while keeping the project on track. Additional revisions beyond this can be arranged at an hourly rate.',
      },
      {
        q: 'What counts as a revision?',
        a: 'A revision round includes all feedback submitted at one time. We recommend collecting all your thoughts before submitting to make the most of each round. Major changes to the concept direction may require additional fees.',
      },
    ],
  },
  {
    category: 'Payment',
    questions: [
      {
        q: 'What are your payment terms?',
        a: 'We require a 50% non-refundable deposit to begin work, with the remaining 50% due upon project completion before final files are released. We accept bank transfer, PayPal, and other agreed payment methods.',
      },
      {
        q: 'Do you offer payment plans?',
        a: 'For larger projects, we can discuss payment milestones. Typically, this involves splitting payments across key project phases. Contact us to discuss options for your specific project.',
      },
      {
        q: 'What is your refund policy?',
        a: 'The initial deposit is non-refundable once work begins. If you cancel before work starts, a partial refund may be possible. After final file delivery, no refunds are issued. Please review our Terms of Service for complete details.',
      },
    ],
  },
  {
    category: 'Communication',
    questions: [
      {
        q: 'How will we communicate during the project?',
        a: 'We primarily communicate via email for official project discussions and documentation. For quick questions, we can also use Instagram DMs or Facebook Messenger. You can expect responses within 24-48 business hours.',
      },
      {
        q: 'What are your working hours?',
        a: 'Our business hours are Monday-Friday 9:00 AM - 6:00 PM, and Saturday 10:00 AM - 2:00 PM. We are closed on Sundays. Messages received outside these hours will be responded to on the next business day.',
      },
    ],
  },
  {
    category: 'Files & Deliverables',
    questions: [
      {
        q: 'What file formats will I receive?',
        a: 'Final deliverables typically include PNG, JPG, PDF, and source files (PSD, AI) as appropriate for your project. Specific formats can be discussed during the proposal stage to ensure you get exactly what you need.',
      },
      {
        q: 'Do I own the final designs?',
        a: 'Upon full payment, you receive the rights to use the final approved designs for their intended purpose. However, preliminary concepts and unused designs remain our property. We also retain the right to showcase work in our portfolio.',
      },
    ],
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-border/50">
    <button
      onClick={onClick}
      className="w-full py-5 flex items-center justify-between text-left group"
      data-testid={`faq-item-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
    >
      <span className="font-medium text-foreground pr-8 group-hover:text-primary transition-colors">
        {question}
      </span>
      <ChevronDown
        className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
          isOpen ? 'rotate-180 text-primary' : ''
        }`}
      />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <p className="pb-5 text-sm text-muted-foreground leading-relaxed">
            {answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-8" data-testid="faq-page">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-primary" />
            </div>
            <h1
              data-testid="faq-title"
              className="font-['Sora'] text-3xl sm:text-4xl font-bold text-foreground tracking-tight"
            >
              Frequently Asked Questions
            </h1>
            <p className="text-muted-foreground mt-4 max-w-lg mx-auto">
              Find answers to common questions about working with DDesign Creative Studio.
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-10">
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h2 className="font-['Sora'] text-lg font-semibold text-primary mb-4">
                  {category.category}
                </h2>
                <div className="bg-card border border-border/50 rounded-lg overflow-hidden">
                  {category.questions.map((item, questionIndex) => (
                    <FAQItem
                      key={questionIndex}
                      question={item.q}
                      answer={item.a}
                      isOpen={openItems[`${categoryIndex}-${questionIndex}`]}
                      onClick={() => toggleItem(categoryIndex, questionIndex)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="text-center pt-8">
            <p className="text-muted-foreground mb-4">
              Still have questions?
            </p>
            <Link
              to="/start-project"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Get in touch
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
